import { useCallback, useState } from "react";
import { TableRow, TableCell } from "components/table";
import { FileType } from "reedy-data/models";
import { TruncatedText } from "components/TruncatedText";
import { ChevronDown, AlertTriangle, InfoIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "components/ui/badge";
import { cn } from "utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { TFunction } from "i18next";
import { Button } from "components/button";
import { Text } from "components/text";
import { useApi } from "hooks/useApi";
import { logError } from "shared/services/ErrorReporting";
import { toast } from "utils/toast";
import { saveAs } from "file-saver";

export interface UploadReportProduct {
  isbn: string;
  title?: string;
  author?: string[];
  productGroupDescription?: string;
  failed?: boolean;
  publishedAt?: string;
  chunkFileName: string;
  errors: string[];
}

interface OrganisationOnixUploadFileReportDialogTableRowProps {
  product: UploadReportProduct;
}

const getBadgeProperties = (
  product: UploadReportProduct,
  t: TFunction,
): {
  variant: "error" | "warning" | "success";
  text: string;
} => {
  if (product.failed) {
    return {
      variant: "error",
      text: t("settings:organisation.onix.misc.badge.failed"),
    };
  }

  if (product.errors.length > 0) {
    return {
      variant: "warning",
      text: t("settings:organisation.onix.misc.badge.warning"),
    };
  }

  return {
    variant: "success",
    text: t("settings:organisation.onix.misc.badge.success"),
  };
};

export const OrganisationOnixUploadFileReportDialogTableRow = ({
  product,
}: OrganisationOnixUploadFileReportDialogTableRowProps) => {
  const { t } = useTranslation(["settings"]);
  const [showErrors, setShowErrors] = useState(false);

  const api = useApi();
  const badgeProps = getBadgeProperties(product, t);

  const handleDownloadChunkClick = useCallback(() => {
    api.files
      .getFile(product.chunkFileName, FileType.ONIX_CHUNK)
      .then((blob) => {
        if (!blob) {
          return;
        }
        const file = new Blob([blob], { type: blob.type });
        saveAs(file, product.chunkFileName);
      })
      .catch((error) => {
        const errorMessage = error?.message;
        logError(errorMessage);
        toast.error(errorMessage);
      });
  }, [product, api]);

  return (
    <>
      <TableRow>
        <TableCell>
          <TruncatedText text={product.isbn} />
        </TableCell>
        <TableCell>
          <TruncatedText
            text={
              Array.isArray(product.author)
                ? product.author.join(", ")
                : product.author
            }
          />
        </TableCell>
        <TableCell>
          <TruncatedText text={product.title} />
        </TableCell>
        <TableCell>
          <TruncatedText text={product.productGroupDescription} />
        </TableCell>
        <TableCell>
          <TruncatedText text={product.publishedAt} />
        </TableCell>
        <TableCell>
          <div className="flex items-center">
            <Badge variant={badgeProps.variant} className="ml-2">
              {badgeProps.text}
            </Badge>
            {product.errors.length > 0 || product.failed ? (
              <div className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      aria-label={t(
                        "settings:organisation.onix.misc.downloadChunk",
                      )}
                      type="button"
                      size="small"
                      icon="download"
                      variant="ghost"
                      className="mb-0"
                      onClick={handleDownloadChunkClick}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    {t("settings:organisation.onix.misc.downloadChunk")}
                  </TooltipContent>
                </Tooltip>
                {product.errors.length > 0 ? (
                  <button
                    className="flex"
                    onClick={() => setShowErrors(!showErrors)}
                    aria-label={`${product.title || product.isbn} errors`}
                    type="button"
                  >
                    <ChevronDown
                      className={cn(
                        "w-4 h-4 transform transition-transform duration-300 text-secondary-500",
                        showErrors && "rotate-180",
                      )}
                    />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </TableCell>
      </TableRow>

      {showErrors && product.errors.length > 0 && (
        <TableRow className="bg-secondary-50">
          <TableCell colSpan={6} className="p-4">
            <Text as="div" variant="body">
              <Text as="h4" variant="label" className="mb-2">
                {t("settings:organisation.onix.misc.errors")}:
              </Text>
              <ul className="pl-5">
                {product.errors.map((error) => {
                  if (error.split("|").length < 4) {
                    return (
                      <li
                        key={error}
                        className="mb-1 break-words hyphens-auto flex items-start"
                      >
                        <AlertTriangle className="h-4 w-4 text-system-error" />
                        <span>{error}</span>
                      </li>
                    );
                  }

                  const [type, origin, position, message] = error.split("|");
                  const [
                    // biome-ignore lint/correctness/noUnusedVariables: positional destructuring; only line and character are shown
                    file,
                    line,
                    character,
                  ] = position.split(":");
                  return (
                    <li
                      key={`${position}-${message}`}
                      className="mb-1 break-words hyphens-auto flex items-start"
                    >
                      <Tooltip>
                        <TooltipTrigger className="mr-2 mt-0.5 flex-shrink-0">
                          {type === "ERROR" ? (
                            <AlertTriangle className="h-4 w-4 text-system-error" />
                          ) : (
                            <InfoIcon className="h-4 w-4 text-amber-500" />
                          )}
                        </TooltipTrigger>
                        <TooltipContent>
                          {type} - {origin}
                        </TooltipContent>
                      </Tooltip>
                      <span>
                        {line}:{character} - {message}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Text>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
